// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file ToggleButtonGroup.tsx
 * @input Uses React Context, ToggleButton children
 * @output Exports ToggleButtonGroup component and types
 * @position Groups toggle buttons for single or multi-select behavior
 *
 * Uses a discriminated union on `type` to enforce type-safe value/onChange:
 * - type='single' → value: string | null, onChange: (v: string | null) => void
 * - type='multiple' → value: string[], onChange: (v: string[]) => void
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/ToggleButton/index.ts
 * - /apps/storybook/stories/ToggleButton.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/ToggleButton/ (showcase blocks)
 */
import { createContext, useCallback, use, useMemo } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { spacingVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";

// =============================================================================
// Context
// =============================================================================
import { jsx as _jsx } from "react/jsx-runtime";
const ToggleButtonGroupContext = /*#__PURE__*/createContext(null);
ToggleButtonGroupContext.displayName = 'ToggleButtonGroupContext';

/**
 * Hook for ToggleButton to read group context.
 * Returns null when used outside a group.
 */
export function useToggleButtonGroup() {
  return use(ToggleButtonGroupContext);
}

// =============================================================================
// Styles
// =============================================================================

const styles = {
  group: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "xzye2dw",
    $$css: true
  },
  vertical: {
    kXwgrk: "xdt5ytf",
    kGNEyG: "x1qjc9v5",
    $$css: true
  }
};

// =============================================================================
// Props — Discriminated Union
// =============================================================================

/**
 * Single-select group props.
 * Only one button can be active at a time. Clicking the active
 * button deselects it (value becomes null).
 */

/**
 * Multi-select group props.
 * Multiple buttons can be active simultaneously.
 */

/** Discriminated union of single and multiple group props. */

// =============================================================================
// Component
// =============================================================================

/**
 * Groups toggle buttons for exclusive (single) or multi-select behavior.
 *
 * Uses a discriminated union on `type` for type-safe value/onChange:
 * - `'single'` (default): `value: string | null`, click active deselects
 * - `'multiple'`: `value: string[]`, toggles individual items
 *
 * @example
 * ```
 * const [view, setView] = useState<string | null>('grid');
 * <ToggleButtonGroup value={view} onChange={setView} label="View mode">
 *   <ToggleButton value="list" label="List" icon={<ListIcon />} />
 *   <ToggleButton value="grid" label="Grid" icon={<GridIcon />} />
 * </ToggleButtonGroup>
 * ```
 */
export function ToggleButtonGroup(props) {
  const {
    children,
    label,
    orientation = 'horizontal',
    size,
    isDisabled = false,
    xstyle,
    'data-testid': testId
  } = props;
  const isMultiple = props.type === 'multiple';
  const selectedValues = useMemo(() => {
    if (isMultiple) {
      return new Set(props.value);
    }
    const singleValue = props.value;
    return singleValue != null ? new Set([singleValue]) : new Set();
  }, [isMultiple, props.value]);
  const toggle = useCallback(itemValue => {
    if (isMultiple) {
      const current = props.value;
      const onChange = props.onChange;
      if (current.includes(itemValue)) {
        onChange(current.filter(v => v !== itemValue));
      } else {
        onChange([...current, itemValue]);
      }
    } else {
      const current = props.value;
      const onChange = props.onChange;
      // Allow deselection: clicking active → null
      onChange(current === itemValue ? null : itemValue);
    }
  }, [isMultiple, props.value, props.onChange]);
  const contextValue = useMemo(() => ({
    selectedValues,
    toggle,
    size,
    isDisabled
  }), [selectedValues, toggle, size, isDisabled]);
  return /*#__PURE__*/_jsx(ToggleButtonGroupContext, {
    value: contextValue,
    children: /*#__PURE__*/_jsx("div", {
      role: "group",
      "aria-label": label,
      "data-testid": testId,
      ...mergeProps(themeProps('toggle-button-group'), stylex.props(styles.group, orientation === 'vertical' && styles.vertical, xstyle)),
      children: children
    })
  });
}
ToggleButtonGroup.displayName = 'ToggleButtonGroup';